import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useApp } from "@/context/AppProvider";
import { Reveal } from "@/components/motion";
import { EditableText, ListControls, AddButton } from "@/components/editable";
import { TEMPLATES } from "@/content";
import { Award } from "lucide-react";

const Counter = ({ value, prefix = "", suffix = "", ar = false }) => {
  const target = Number(value) || 0;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const duration = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  const num = ar ? display.toLocaleString("ar-EG") : display.toLocaleString("en-US");
  return (
    <span ref={ref}>
      {prefix}
      {num}
      {suffix}
    </span>
  );
};

export const Impact = () => {
  const { tr, ed, lang, editMode, moveItem, removeItem, addItem } = useApp();
  const path = ["impact", "stats"];
  const stats = tr.impact.stats;

  return (
    <section id="impact" data-testid="impact" className="relative py-24 md:py-32 lg:py-40 overflow-hidden" style={{ backgroundColor: "var(--ink)" }}>
      <div className="absolute inset-0 opacity-[0.06]" style={{ background: "radial-gradient(700px 500px at 80% 10%, var(--gold), transparent 60%)" }} />
      <div className="container-lux relative">
        <Reveal className="max-w-2xl mb-16">
          <EditableText as="p" path={["impact", "eyebrow"]} value={tr.impact.eyebrow} className="eyebrow mb-4 inline-block" />
          <EditableText as="h2" path={["impact", "title"]} value={tr.impact.title} className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.1] block" style={{ color: "#fff" }} />
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className={`relative border-t pt-6 ${editMode ? "editing-outline" : ""}`} style={{ borderColor: "rgba(255,255,255,0.14)" }} data-testid={`impact-stat-${i}`}>
                {editMode && (
                  <ListControls
                    className="absolute -top-3 end-0"
                    onUp={() => moveItem(path, i, -1)}
                    onDown={() => moveItem(path, i, 1)}
                    onDelete={() => removeItem(path, i)}
                    canUp={i > 0}
                    canDown={i < stats.length - 1}
                    testid={`impact-controls-${i}`}
                  />
                )}
                {editMode ? (
                  <p className="font-display text-3xl md:text-4xl mb-3 flex flex-wrap items-baseline gap-1 text-gradient-gold">
                    <EditableText path={[...path, i, "prefix"]} value={s.prefix} />
                    <EditableText path={[...path, i, "value"]} value={String(s.value)} />
                    <EditableText path={[...path, i, "suffix"]} value={s.suffix} />
                  </p>
                ) : (
                  <p className="font-display text-4xl md:text-5xl lg:text-6xl mb-3 text-gradient-gold">
                    <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} ar={lang === "ar"} />
                  </p>
                )}
                <EditableText as="p" multiline path={[...path, i, "label"]} value={s.label} className="font-body text-sm leading-relaxed block" style={{ color: "rgba(255,255,255,0.65)" }} />
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-10">
          <AddButton onClick={() => addItem(path, TEMPLATES.impactStat.en, TEMPLATES.impactStat.ar)} label={ed.addItem} testid="add-impact" className="!text-white !border-white/40" />
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 flex items-start gap-4 max-w-2xl">
            <Award size={22} style={{ color: "var(--gold)" }} className="mt-1 shrink-0" />
            <EditableText as="p" multiline path={["impact", "recognition"]} value={tr.impact.recognition} className="font-body text-lg leading-relaxed block" style={{ color: "rgba(255,255,255,0.85)" }} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
